import { Button, FileUploader, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import useCokitchenContext from 'contexts/cokitchen.context';
import { STORAGE_KEYS } from 'utils/constants';
import { CoKitchenType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import MultiInput from 'components/base/multi-input';
import useDashboardContext from 'contexts/dashboard.context';
import AddressInput from 'components/address-input';

const EditKitchenDetails = ({ onCancel }: { onCancel: () => void }) => {
  const { editCokitchenForm, setEditCokitchenForm } = useCokitchenContext();
  const { activeCokitchen, setActiveCokitchen } = useDashboardContext();
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  const [latLng, setLatLng] = useState<{ lat: string; lng: string }>();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEditCokitchenForm({
      ...editCokitchenForm,
      lat: latLng?.lat || activeCokitchen.lat,
      lng: latLng?.lng || activeCokitchen.lng
    });
  }, [latLng]);

  const handleCsNumberDelete = (value: string) =>
    setEditCokitchenForm({
      ...editCokitchenForm,
      customer_service_phone_number: editCokitchenForm.customer_service_phone_number.filter(
        (num) => num !== value
      )
    });

  const handleCsNumberChange = (value: string) => {
    if (value?.length > 10) {
      setEditCokitchenForm({
        ...editCokitchenForm,
        customer_service_phone_number: [...editCokitchenForm.customer_service_phone_number, value]
      });
      return true;
    }
  };

  const handleCsWhatsAppDelete = (value: string) =>
    setEditCokitchenForm({
      ...editCokitchenForm,
      customer_service_whatsapp: editCokitchenForm.customer_service_whatsapp.filter(
        (num) => num !== value
      )
    });

  const handleCsWhatsAppChange = (value: string) => {
    if (value?.length > 10) {
      setEditCokitchenForm({
        ...editCokitchenForm,
        customer_service_whatsapp: [...editCokitchenForm.customer_service_whatsapp, value]
      });
      return true;
    }
  };

  const handleFdNumberDelete = (value: string) =>
    setEditCokitchenForm({
      ...editCokitchenForm,
      frontdesk_phone_number: editCokitchenForm.frontdesk_phone_number.filter(
        (num) => num !== value
      )
    });

  const handleFdNumberChange = (value: string) => {
    if (value?.length > 10) {
      setEditCokitchenForm({
        ...editCokitchenForm,
        frontdesk_phone_number: [...editCokitchenForm.frontdesk_phone_number, value]
      });
      return true;
    }
  };

  const handleFdWhatsAppDelete = (value: string) =>
    setEditCokitchenForm({
      ...editCokitchenForm,
      frontdesk_whatsapp: editCokitchenForm.frontdesk_whatsapp.filter((num) => num !== value)
    });

  const handleFdWhatsAppChange = (value: string) => {
    if (value?.length > 10) {
      setEditCokitchenForm({
        ...editCokitchenForm,
        frontdesk_whatsapp: [...editCokitchenForm.frontdesk_whatsapp, value]
      });
      return true;
    }
  };

  const validateNumber = (value: string) => value === '' || !isNaN(Number(value));

  useEffect(() => {
    setEditCokitchenForm(activeCokitchen);
  }, [activeCokitchen]);

  const updateCokitchen = async () => {
    setLoading(true);
    const payload: Partial<CoKitchenType> = Object.entries(editCokitchenForm).reduce(
      (acc: Partial<CoKitchenType>, [key, value]) => {
        if (activeCokitchen[key as keyof CoKitchenType] !== value) {
          acc[key as keyof CoKitchenType] = value;
        }
        return acc;
      },
      {}
    );

    payload.cokitchen_id = activeCokitchen.id;
    payload.event_image = editCokitchenForm?.event_image;
    payload.cover_images = editCokitchenForm?.cover_images ?? [];
    payload.cover_videos = editCokitchenForm?.cover_videos ?? [];

    try {
      const res: any = await apiService.put('marketing/pop/cokitchen', { data: payload });
      if (res.data) {
        toast(
          <ToastMessage type='success' message={`${activeCokitchen?.name} updated successfully`} />
        );
      }
      setActiveCokitchen({ ...activeCokitchen, ...payload });
      onCancel();
    } catch {
      toast(<ToastMessage type='error' message='Error updating cokitchen details' />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Input
          small
          type='text'
          label='Cokitchen name '
          value={editCokitchenForm?.name}
          onChange={(e) => setEditCokitchenForm({ ...editCokitchenForm, name: e.target.value })}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <AddressInput
          label='Cokitchen Address'
          placeholder='Enter address'
          address={editCokitchenForm.address}
          setAddress={(e) =>
            setEditCokitchenForm({
              ...editCokitchenForm,
              address: e
            })
          }
          setLatLng={setLatLng}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Input
          small
          type='text'
          value={editCokitchenForm.description}
          label='Cokitchen Tagline'
          onChange={(e) =>
            setEditCokitchenForm({
              ...editCokitchenForm,
              description: e.target.value
            })
          }
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <MultiInput
          label='Customer Service Number'
          placeholder={
            editCokitchenForm.customer_service_phone_number?.length
              ? 'Add another number'
              : 'Add a number'
          }
          values={editCokitchenForm.customer_service_phone_number}
          onDelete={handleCsNumberDelete}
          onChange={handleCsNumberChange}
          validate={validateNumber}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <MultiInput
          label='Customer Service Number (WhatsApp)'
          placeholder={
            editCokitchenForm.customer_service_whatsapp?.length
              ? 'Add another number'
              : 'Add a number'
          }
          values={editCokitchenForm.customer_service_whatsapp}
          onDelete={handleCsWhatsAppDelete}
          onChange={handleCsWhatsAppChange}
          validate={validateNumber}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <MultiInput
          label='Frontdesk Number'
          placeholder={
            editCokitchenForm.frontdesk_phone_number?.length ? 'Add another number' : 'Add a number'
          }
          values={editCokitchenForm.frontdesk_phone_number}
          onDelete={handleFdNumberDelete}
          onChange={handleFdNumberChange}
          validate={validateNumber}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <MultiInput
          label='Frontdesk Number (WhatsApp)'
          placeholder={
            editCokitchenForm.frontdesk_whatsapp?.length ? 'Add another number' : 'Add a number'
          }
          values={editCokitchenForm.frontdesk_whatsapp}
          onDelete={handleFdWhatsAppDelete}
          onChange={handleFdWhatsAppChange}
          validate={validateNumber}
        />
      </div>
      <div className='mb-5'>
        <FileUploader
          token={token}
          setFileUrls={(urls) => {
            setEditCokitchenForm({ ...editCokitchenForm, event_image: urls?.[0] || '' });
          }}
          fileUrls={editCokitchenForm.event_image ? [editCokitchenForm.event_image] : []}
        />
      </div>

      <div>
        <FileUploader
          key={editCokitchenForm.cover_images.length}
          token={token}
          fileType='image'
          isMultiple
          maxNumberOfFiles={6 - editCokitchenForm.cover_images.length}
          fileUrls={editCokitchenForm.cover_images}
          setFileUrls={(urls) => {
            setEditCokitchenForm({ ...editCokitchenForm, cover_images: urls });
          }}
          label={
            <div className='mb-2'>
              <h3 className='font-medium text-sm'>Upload Display Images</h3>
              <p className='text-[10px] text-[#595959] mt-1'>Maximum of 6 images</p>
            </div>
          }
          disabled={editCokitchenForm.cover_images.length >= 6}
        />

        {editCokitchenForm.cover_images.length > 0 && (
          <div className='mt-4 flex flex-wrap gap-3'>
            {editCokitchenForm.cover_images.map((image, index) => (
              <div key={index} className='relative w-[160px] h-[160px]'>
                <img
                  src={image}
                  alt={`cover-${index}`}
                  className='w-full h-full object-cover rounded-[10px]'
                />
                <button
                  onClick={() => {
                    const updated = editCokitchenForm.cover_images.filter((_, i) => i !== index);
                    setEditCokitchenForm({ ...editCokitchenForm, cover_images: updated });
                  }}
                  className='absolute top-1 right-1 text-xs bg-black bg-opacity-50 text-white rounded px-1'>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <FileUploader
          key={editCokitchenForm.cover_videos.length}
          token={token}
          fileType='video'
          isMultiple
          maxNumberOfFiles={6 - editCokitchenForm.cover_videos.length}
          fileUrls={editCokitchenForm.cover_videos}
          setFileUrls={(urls) => {
            setEditCokitchenForm({ ...editCokitchenForm, cover_videos: urls });
          }}
          label={
            <div className='mb-2'>
              <h3 className='font-medium text-sm'>Upload Display Videos</h3>
              <p className='text-[10px] text-[#595959] mt-1'>Maximum of 6 videos</p>
            </div>
          }
          disabled={editCokitchenForm.cover_videos.length >= 6}
          apiUrl='https://file-api.getfoodcourt.com/upload-video'
        />

        {editCokitchenForm.cover_videos.length > 0 && (
          <div className='mt-4 flex flex-wrap gap-3'>
            {editCokitchenForm.cover_videos.map((video, index) => (
              <div key={index} className='relative w-[160px] h-[160px]'>
                <video src={video} controls className='w-full h-full object-cover rounded-[10px]' />
                <button
                  onClick={() => {
                    const updated = editCokitchenForm.cover_videos.filter((_, i) => i !== index);
                    setEditCokitchenForm({ ...editCokitchenForm, cover_videos: updated });
                  }}
                  className='absolute top-1 right-1 text-xs bg-black bg-opacity-50 text-white rounded px-1'>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='flex gap-6 items-center justify-end py-5 mt-4'>
        <Button
          onClick={onCancel}
          small
          variant='link'
          color='primary'
          className='text-xs max-w-[150px]'>
          Cancel
        </Button>
        <Button
          onClick={updateCokitchen}
          small
          variant='block'
          color='primary'
          className='text-xs max-w-[150px]'
          loading={loading}>
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default EditKitchenDetails;
